import { UserFileTypesLookup } from '@eluve/graphql-types';
import { calculateFileMd5 } from '@eluve/utils';

import { TsRestClient } from './client';

type UploadUserFileToGcsArgs = {
  apiClient: TsRestClient;
  tenantId: string;
  file: File;
  fileType: (typeof UserFileTypesLookup)[keyof typeof UserFileTypesLookup];
  originalFileName?: string;
};

export const uploadUserFileToGcs = async ({
  apiClient,
  tenantId,
  file,
  fileType,
  originalFileName,
}: UploadUserFileToGcsArgs): Promise<string> => {
  const fileHash = await calculateFileMd5(file);

  // Derive extension/content-type from the file rather than assuming PDF — the
  // GCS write URL is signed without a pinned content-type, so the PUT below can
  // carry the real type. Falls back when the browser gives no name/type.
  const extension = file.name.includes('.')
    ? file.name.split('.').pop()!.toLowerCase()
    : 'pdf';
  const contentType = file.type || 'application/octet-stream';

  const prepareResponse = await apiClient.users.prepareUserFileUpload({
    params: { tenantId },
    body: {
      extension,
      fileType,
      fileName: file.name,
      fileSize: file.size,
      fileHash,
      ...(originalFileName !== undefined && { originalFileName }),
    },
  });

  if (prepareResponse.status !== 200) {
    throw new Error('Failed to prepare file upload');
  }

  const { uploadUrl, userUploadId } = prepareResponse.body;

  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });

  if (!uploadResponse.ok) {
    await apiClient.users.markUserFileUploadFailed({
      params: { tenantId },
      body: { userUploadId },
    });
    throw new Error('Failed to upload file');
  }

  await apiClient.users.markUserFileUploadComplete({
    params: { tenantId },
    body: { userUploadId },
  });

  return userUploadId;
};
